import { Interval } from '@ui/constants/poll';
import { useClosingOrdersStore } from '@ui/features/Websocket/useClosingOrdersStore';
import { usePendingLimitOrdersStore } from '@ui/features/Websocket/usePendingLimitOrdersStore';
import { useBackendPairsAll } from '@ui/services/backend/hooks/getPairs/useBackendPairsAll';
import { useGetLimitOrdersBatch } from '@ui/services/contract/hooks/LimitOrderFacet/useGetLimitOrdersBatch/useGetLimitOrdersBatch';
import { useMemo } from 'react';
import type { Address } from 'viem';

export const useTradeOrders = (userAddress?: Address) => {
  const { data: pairs } = useBackendPairsAll();

  const pendingLimitOrders = usePendingLimitOrdersStore((state) =>
    state.usePendingLimitOrders(),
  );
  const closingOrders = useClosingOrdersStore((s) => s.useClosingOrders());

  const { data: limitOrdersQueryData } = useGetLimitOrdersBatch(
    useMemo(
      () => ({
        pairs: pairs,
        interval: Interval.SLOW,
        userAddress,
      }),
      [pairs, userAddress],
    ),
  );

  // Combine confirmed limit orders with pending limit orders
  const combinedOrders = useMemo(() => {
    if (!limitOrdersQueryData) {
      return userAddress ? [] : pendingLimitOrders;
    }

    // Create a set of order IDs from the query data
    const confirmedOrderIds = new Set(
      limitOrdersQueryData.map((order) => order.id),
    );

    // Filter out pending limit orders that have the same ID as confirmed orders
    const filteredPendingLimitOrders = userAddress
      ? []
      : pendingLimitOrders.filter(
          (pendingOrder) => !confirmedOrderIds.has(pendingOrder.id),
        );

    // Filter out orders that are in closingOrders
    const closingOrdersSet = userAddress
      ? new Set<string>()
      : new Set(closingOrders);
    const filteredLimitOrdersQueryData = limitOrdersQueryData.filter(
      (order) => !closingOrdersSet.has(order.id),
    );

    // Combine filtered pending limit orders with filtered confirmed orders and sort by timestamp (newest first)
    return [
      ...filteredPendingLimitOrders,
      ...filteredLimitOrdersQueryData,
    ].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  }, [limitOrdersQueryData, pendingLimitOrders, closingOrders]);

  return useMemo(
    () => ({
      data: combinedOrders,
    }),
    [combinedOrders],
  );
};
